import { useState } from "react";
import type { EvidenceInterpretationSummary, LocalEditUndo, SectionSixSimplifiedAssessment, SectionSixSimplifiedClientDraft, SectionSixSimplifiedFields, SimplifiedAssessmentFieldOrigin } from "../domain";
import { localEditSuggestion, sectionSixSimplifiedFieldDefinitions, type SimplifiedFieldDefinition } from "../simplified-assessment";
import { DictationControl } from "./DictationControl";

type StringFields<TFields>={ [K in keyof TFields]:string };
type SharedAssessment<TFields extends StringFields<TFields>>={fields:TFields;fieldOrigins:Partial<Record<keyof TFields,SimplifiedAssessmentFieldOrigin>>;interpretation:EvidenceInterpretationSummary;sourceEvidenceIds:string[];updatedAt:string;updatedBy:string};
type SharedClientDraft<TFields extends StringFields<TFields>>={fields:TFields;generatedAt:string;sourceAssessmentConfirmedAt:string};

function AssessmentField<TFields extends StringFields<TFields>>({
  field,
  value,
  disabled,
  undo,
  onChange,
  onApplySuggestion,
  onUndo,
}:{
  field:SimplifiedFieldDefinition<TFields>;
  value:string;
  disabled:boolean;
  undo?:LocalEditUndo;
  onChange:(value:string,origin:SimplifiedAssessmentFieldOrigin)=>void;
  onApplySuggestion:(value:string,previousValue:string)=>void;
  onUndo:()=>void;
}){
  const [selection,setSelection]=useState({start:value.length,end:value.length});
  const [proposal,setProposal]=useState<string>();
  return <section className="simplified-assessment-field">
    <label>{field.label}<small>{field.hint}</small>
      <textarea
        rows={6}
        disabled={disabled}
        value={value}
        onSelect={event=>setSelection({start:event.currentTarget.selectionStart,end:event.currentTarget.selectionEnd})}
        onChange={event=>{setProposal(undefined);onChange(event.target.value,"Steve")}}
      />
    </label>
    {!disabled&&<div className="simplified-field-tools">
      <DictationControl value={value} cursorStart={selection.start} cursorEnd={selection.end} onAccept={next=>{setProposal(undefined);onChange(next,"Steve")}}/>
      <button className="secondary-button assessment-tool-button" type="button" onClick={()=>setProposal(localEditSuggestion(value))} disabled={!value.trim()}>Local edit suggestion</button>
      {undo?.fieldKey===field.key&&<button className="secondary-button assessment-tool-button" type="button" onClick={onUndo}>Undo edit</button>}
    </div>}
    {proposal!==undefined&&<aside className="local-edit-proposal" aria-live="polite">
      <strong>Proposed replacement</strong>
      <p>This suggestion restructures only the wording already present. Review it before applying.</p>
      <textarea rows={6} value={proposal} onChange={event=>setProposal(event.target.value)}/>
      <div className="button-row">
        <button type="button" onClick={()=>setProposal(undefined)}>Cancel</button>
        <button className="primary-button" type="button" onClick={()=>{onApplySuggestion(proposal,value);setProposal(undefined)}}>Apply suggestion</button>
      </div>
    </aside>}
  </section>;
}

export function SimplifiedAssessmentEditor<TFields extends StringFields<TFields>>({
  assessment,
  definitions,
  locked,
  undo,
  onChange,
  onApplySuggestion,
  onUndo,
  onConfirm,
}:{
  assessment?:SharedAssessment<TFields>;
  definitions:Array<SimplifiedFieldDefinition<TFields>>;
  locked:boolean;
  undo?:LocalEditUndo;
  onChange:(key:keyof TFields,value:string,origin:SimplifiedAssessmentFieldOrigin)=>void;
  onApplySuggestion:(key:keyof TFields,value:string,previousValue:string)=>void;
  onUndo:(key:keyof TFields)=>void;
  onConfirm:()=>void;
}){
  if(!assessment)return <p>Review the evidence and choose Draft from evidence before editing the assessment.</p>;
  return <div className="simplified-assessment-editor">
    {definitions.map(field=><AssessmentField
      key={String(field.key)}
      field={field}
      value={assessment.fields[field.key]}
      disabled={locked}
      undo={undo}
      onChange={(value,origin)=>onChange(field.key,value,origin)}
      onApplySuggestion={(value,previous)=>onApplySuggestion(field.key,value,previous)}
      onUndo={()=>onUndo(field.key)}
    />)}
    <button className="primary-button" disabled={locked} onClick={onConfirm}>Confirm assessment</button>
  </div>;
}

export function SimplifiedSectionSixAssessmentEditor(props:{
  assessment?:SectionSixSimplifiedAssessment;
  locked:boolean;
  undo?:LocalEditUndo;
  onChange:(key:keyof SectionSixSimplifiedFields,value:string,origin:SimplifiedAssessmentFieldOrigin)=>void;
  onApplySuggestion:(key:keyof SectionSixSimplifiedFields,value:string,previousValue:string)=>void;
  onUndo:(key:keyof SectionSixSimplifiedFields)=>void;
  onConfirm:()=>void;
}){
  return <SimplifiedAssessmentEditor {...props} definitions={sectionSixSimplifiedFieldDefinitions}/>;
}

function DictatableClientField<TFields extends StringFields<TFields>>({field,value,disabled,onChange}:{field:SimplifiedFieldDefinition<TFields>;value:string;disabled:boolean;onChange:(value:string)=>void}){
  const [selection,setSelection]=useState({start:value.length,end:value.length});
  return <label>{field.label}<textarea rows={6} disabled={disabled} value={value} onSelect={event=>setSelection({start:event.currentTarget.selectionStart,end:event.currentTarget.selectionEnd})} onChange={event=>onChange(event.target.value)}/>{!disabled&&<DictationControl value={value} cursorStart={selection.start} cursorEnd={selection.end} onAccept={onChange}/>}</label>;
}

export function SimplifiedClientEditor<TFields extends StringFields<TFields>>({draft,definitions,locked,approvalDisabled=false,onChange,onApprove}:{draft?:SharedClientDraft<TFields>;definitions:Array<SimplifiedFieldDefinition<TFields>>;locked:boolean;approvalDisabled?:boolean;onChange:(key:keyof TFields,value:string)=>void;onApprove:()=>void}){
  if(!draft)return <p>Confirm the four-field assessment before reviewing the client report.</p>;
  return <div className="simplified-client-editor">
    {definitions.map(field=><DictatableClientField key={String(field.key)} field={field} value={draft.fields[field.key]} disabled={locked} onChange={value=>onChange(field.key,value)}/>)}
    <button className="primary-button" disabled={locked||approvalDisabled} onClick={onApprove}>Approve client report</button>
  </div>;
}

export function SimplifiedSectionSixClientEditor(props:{draft?:SectionSixSimplifiedClientDraft;locked:boolean;approvalDisabled?:boolean;onChange:(key:keyof SectionSixSimplifiedFields,value:string)=>void;onApprove:()=>void}){
  return <SimplifiedClientEditor {...props} definitions={sectionSixSimplifiedFieldDefinitions}/>;
}
